import React, { useState, useEffect, useRef, useMemo } from "react";
import ImageCarousel from "./ImageCarousel";
import AdminApp from "./admin/AdminApp";
import { getApiUrl } from "./config/api";
import { UserLogin, UserSignup } from "./UserAuth";
import ShareButton from "./ShareButton";
import Marquee from "./Marquee";
import Embed from "./components/Embed";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useParams,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

// --- SEO HELPER COMPONENT ---
const MetaTags = ({ title, description }) => {
  useEffect(() => {
    // The base title that will be appended to all page titles
    const baseTitle =
      "Amdavad Post: અમદાવાદના તાજા ગુજરાતી સમાચાર, ગુજરાત અને દેશ-દુનિયા Live";
    document.title = `${title} | ${baseTitle}`;

    // Find and remove any existing meta description tag
    const existingMetaTag = document.querySelector('meta[name="description"]');
    if (existingMetaTag) {
      existingMetaTag.remove();
    }

    // Create and append the new meta description tag
    const metaDescriptionTag = document.createElement("meta");
    metaDescriptionTag.name = "description";
    metaDescriptionTag.content = description;
    document.head.appendChild(metaDescriptionTag);

    // Cleanup function to remove the tag when the component unmounts
    return () => {
      document.head.removeChild(metaDescriptionTag);
    };
  }, [title, description]); // Rerun effect if title or description changes

  return null; // This component does not render any visible UI
};

const NewsSection = ({
  topNews,
  trendingNews,
  headings,
  lang,
  imageVersion,
}) => {
  const navigate = useNavigate();

  const handleCardKeyPress = (e, slug) => {
    if (e.key === "Enter" || e.key === " ") {
      navigate(`/news/${slug}`);
    }
  };

  return (
    <div className="container mx-auto px-4 py-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <h2 className="text-2xl font-bold mb-4 border-b-2 border-red-600 pb-2">
          {headings.topNews}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {topNews.map((news) => (
            <div
              key={news.id}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => handleCardKeyPress(e, news.slug)}
              onClick={() => navigate(`/news/${news.slug}`)}
              className="bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 relative cursor-pointer"
            >
              <div className="relative">
                <div className="absolute top-2 right-2 z-20">
                  <ShareButton
                    url={`${window.location.origin}/news/${news.slug}`}
                    title={news.title}
                  />
                </div>
                {/* Use single image for top news (no carousel) */}
                <img
                  src={
                    news.image && news.image.length > 0
                      ? `${news.image[0]}?v=${imageVersion}`
                      : getImageUrl(
                          news,
                          (() => {
                            const categories = parseCategories(news.categories);
                            return categories.length > 0
                              ? categories[0]
                              : "News";
                          })(),
                          imageVersion
                        )
                  }
                  alt={news.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <div className="mb-2 flex flex-wrap gap-2">
                    {(parseCategories(news.categories) || []).map((c, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full capitalize"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  <h3
                    className={`font-bold text-lg mb-2 h-14 overflow-hidden ${
                      lang === "gu" ? "font-noto-gujarati" : "font-sans"
                    }`}
                  >
                    {news.title}
                  </h3>
                  <div className="text-sm text-gray-500 flex justify-between items-center mt-4">
                    <span>{formatDate(news.createdAt)}</span>
                    <span className="font-semibold text-red-700 hover:text-red-900">
                      Read More &rarr;
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h2 className="text-2xl font-bold mb-4 border-b-2 border-red-600 pb-2">
          {headings.trending}
        </h2>
        <div className="space-y-4">
          {trendingNews.map((news) => (
            <div
              key={news.id}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => handleCardKeyPress(e, news.slug)}
              onClick={() => navigate(`/news/${news.slug}`)}
              className="relative rounded-lg cursor-pointer p-2 hover:bg-gray-100 transition-colors flex items-start space-x-4"
            >
              <div className="absolute top-1 right-1 z-20">
                <ShareButton
                  url={`${window.location.origin}/news/${news.slug}`}
                  title={news.title}
                />
              </div>
              {/* Use ImageCarousel for trending news */}
              <div className="w-24 h-16 flex-shrink-0 relative">
                <img
                  src={
                    news.image && news.image.length > 0
                      ? `${news.image[0]}?v=${imageVersion}`
                      : getImageUrl(news, "Trending", imageVersion)
                  }
                  alt={news.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="mb-1 flex flex-wrap gap-1">
                  {(parseCategories(news.categories) || []).map((c, idx) => (
                    <span
                      key={idx}
                      className="text-[10px] font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full capitalize"
                    >
                      {c}
                    </span>
                  ))}
                </div>
                <h4
                  className={`font-semibold leading-tight ${
                    lang === "gu" ? "font-noto-gujarati" : "font-sans"
                  }`}
                >
                  {news.title}
                </h4>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(news.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
        {/* Embed: prefer first trending post's embedUrl, otherwise fallback to REACT_APP_X_EMBED_URL */}
        {/* {(() => {
          try {
            const urlFromTrending = (trendingNews || []).find(
              (n) => n && (n.embedUrl || n.embedURL || n.embed_url)
            )?.embedUrl;
            const embedUrlToUse =
              urlFromTrending || process.env.REACT_APP_X_EMBED_URL || "";
            return embedUrlToUse ? (
              <div className="mt-6">
                <Embed url={embedUrlToUse} className="w-full" />
              </div>
            ) : null;
          } catch (err) {
            console.error(
              "Failed to determine embedUrl for trending embed:",
              err
            );
            return null;
          }
        })()} */}
      </div>
    </div>
  );
};
const mockData = {
  en: {
    buttons: { epaper: "E-paper" },
    nav: [
      "Home",
      "Videos",
      "Shorts",
      "Ahmedabad",
      "Gujarat",
      "India",
      "World",
      "Business",
      "Sports",
      "Entertainment",
      "Lifestyle",
      "Technology",
    ],
    heroArticle: { readMore: "Read More" },
    headings: {
      topNews: "Top News",
      trending: "Trending",
      videos: "Videos",
      reels: "Reels",
      relatedNews: "Related News",
    },
    footer: {
      services: "Services",
      legal: "Legal",
      links: {
        epaper: "E-Paper",
        terms: "Terms of Use",
        privacy: "Privacy Policy",
        cookie: "Cookie Policy",
      },
      copyright: "Amdavad Post. All Rights Reserved.",
    },
  },
  gu: {
    buttons: { epaper: "ઈ-પેપર" },
    nav: [
      "હોમ",
      "વીડિયો",
      "શોર્ટ્સ",
      "અમદાવાદ",
      "ગુજરાત",
      "ભારત",
      "વિશ્વ",
      "બિઝનેસ",
      "સ્પોર્ટ્સ",
      "મનોરંજન",
      "જીવનશૈલી",
      "ટેકનોલોજી",
    ],
    heroArticle: { readMore: "વધુ વાંચો" },
    headings: {
      topNews: "મુખ્ય સમાચાર",
      trending: "ટ્રેન્ડિંગ",
      videos: "વીડિયો",
      reels: "રીલ્સ",
      relatedNews: "સંબંધિત સમાચાર",
    },
    footer: {
      services: "સેવાઓ",
      legal: "કાનૂની",
      links: {
        epaper: "ઈ-પેપર",
        archives: "આર્કાઇવ્સ",
        terms: "ઉપયોગની શરતો",
        privacy: "ગોપનીયતા નીતિ",
        cookie: "કૂકી નીતિ",
      },
      copyright: "અમદાવાદ પોસ્ટ. સર્વાધિકાર સુરક્ષિત.",
    },
  },
};

// --- STYLES FOR HIDING SCROLLBAR ---
const customStyles = `
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
`;

// --- HELPER FUNCTIONS ---
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname === "youtu.be") return urlObj.pathname.slice(1);
    if (urlObj.hostname.includes("youtube.com")) {
      const videoId = urlObj.searchParams.get("v");
      if (videoId) return videoId;
      const pathParts = urlObj.pathname.split("/");
      if (pathParts[1] === "shorts") return pathParts[2];
    }
  } catch (error) {
    console.error("Invalid URL:", url);
  }
  return null;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Prefer published date (or returned 'date') over createdAt for display
const formatArticleDate = (article) => {
  if (!article) return "";
  // If backend returns a DATEONLY string like '2025-10-08'
  if (article.publishedDate) return formatDate(article.publishedDate);
  // If backend returns a pre-formatted dd/mm/yyyy in `date`, try to parse it
  if (article.date) {
    const s = String(article.date).trim();
    const parts = s.split("/");
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
      if (!isNaN(dd) && !isNaN(mm) && !isNaN(yyyy)) {
        return new Date(yyyy, mm - 1, dd).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
    }
    // fallback: return the raw string
    return s;
  }
  // fallback to createdAt
  return formatDate(article.createdAt);
};

// Small helper: escape HTML and convert *text* to <strong>text</strong>
const escapeHtml = (unsafe) => {
  if (!unsafe) return "";
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const convertStarsToHtml = (text) => {
  if (!text) return "";
  const escaped = escapeHtml(text);
  const replaced = escaped.replace(
    /\*(.+?)\*/g,
    (m, p1) => `<strong>${p1}</strong>`
  );
  return replaced.replace(/\r?\n/g, "<br />");
};

// Truncate HTML while preserving tags. Returns a safe HTML string
// that contains up to `maxChars` characters of text (adds ellipsis
// when truncated). Runs in the browser (uses DOM APIs).
const truncateHtml = (html, maxChars) => {
  if (!html) return "";
  try {
    const container = document.createElement("div");
    container.innerHTML = html;
    let count = 0;

    const walk = (node) => {
      if (count >= maxChars) {
        // remove this node entirely if we've already hit the limit
        node.parentNode && node.parentNode.removeChild(node);
        return false;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue || "";
        const remaining = maxChars - count;
        if (text.length > remaining) {
          node.nodeValue = text.slice(0, remaining) + "...";
          count = maxChars;
        } else {
          count += text.length;
        }
        return true;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        // copy list because we may modify children during iteration
        const children = Array.from(node.childNodes);
        for (const child of children) {
          const ok = walk(child);
          if (!ok) {
            // if child indicated to stop, remove any following siblings
            let next = child.nextSibling;
            while (next) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode && toRemove.parentNode.removeChild(toRemove);
            }
            break;
          }
        }
      }
      return true;
    };

    walk(container);
    return container.innerHTML;
  } catch (e) {
    // Fallback: strip tags and truncate plain text
    const textOnly = String(html).replace(/<[^>]*>/g, "");
    return escapeHtml(textOnly.length > maxChars ? textOnly.slice(0, maxChars) + "..." : textOnly).replace(/\r?\n/g, "<br />");
  }
};

// --- SVG ICONS ---
const EpaperIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 mr-2"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.253v11.494m-5.22-6.229h10.44M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

// --- Small DatePicker for Epaper filters ---
const SmallDatePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const ref = useRef();

  useEffect(() => setInputValue(value || ""), [value]);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const parseDDMMYYYY = (s) => {
    const parts = String(s).split("/");
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
      if (!isNaN(dd) && !isNaN(mm) && !isNaN(yyyy))
        return new Date(yyyy, mm - 1, dd);
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDDMMYYYY = (d) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const handlePick = (d) => {
    const val = formatDDMMYYYY(d);
    setInputValue(val);
    onChange(val);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center space-x-2">
        <input
          type="text"
          placeholder="dd/mm/yyyy"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={() => {
            const parsed = parseDDMMYYYY(inputValue);
            if (parsed) onChange(formatDDMMYYYY(parsed));
          }}
          className="p-2 border rounded-md w-40"
        />
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="p-2 bg-yellow-100 rounded border"
        >
          📅
        </button>
      </div>
      {open && (
        <div className="absolute z-40 mt-2 bg-white border rounded shadow p-2">
          <input
            type="date"
            className="p-2 border rounded"
            onChange={(e) => {
              const d = new Date(e.target.value);
              if (!isNaN(d.getTime())) handlePick(d);
            }}
          />
          <div className="mt-2 text-xs text-gray-500">
            Pick a date or type dd/mm/yyyy
          </div>
        </div>
      )}
    </div>
  );
};
const PlayIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-16 w-16 text-white/80"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
      clipRule="evenodd"
    />
  </svg>
);
const FacebookIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v2.385z" />
  </svg>
);
const TwitterIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616v.064c0 2.298 1.634 4.212 3.793 4.649-.65.177-1.354.23-2.06.088.623 1.954 2.427 3.376 4.566 3.415-1.773 1.39-4.012 2.218-6.452 2.218-.42 0-.833-.024-1.237-.072 2.282 1.465 5.002 2.318 7.91 2.318 9.491 0 14.685-7.858 14.685-14.685 0-.224-.005-.447-.014-.668.998-.722 1.864-1.622 2.55-2.65z" />
  </svg>
);
const YoutubeIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
  </svg>
);
// ThreadsIcon component
const ThreadsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 16 16"
    fill="currentColor"
    className="bi bi-threads"
  >
    <path d="M6.321 6.016c-.27-.18-1.166-.802-1.166-.802.756-1.081 1.753-1.502 3.132-1.502.975 0 1.803.327 2.394.948s.928 1.509 1.005 2.644q.492.207.905.484c1.109.745 1.719 1.86 1.719 3.137 0 2.716-2.226 5.075-6.256 5.075C4.594 16 1 13.987 1 7.994 1 2.034 4.482 0 8.044 0 9.69 0 13.55.243 15 5.036l-1.36.353C12.516 1.974 10.163 1.43 8.006 1.43c-3.565 0-5.582 2.171-5.582 6.79 0 4.143 2.254 6.343 5.63 6.343 2.777 0 4.847-1.443 4.847-3.556 0-1.438-1.208-2.127-1.27-2.127-.236 1.234-.868 3.31-3.644 3.31-1.618 0-3.013-1.118-3.013-2.582 0-2.09 1.984-2.847 3.55-2.847.586 0 1.294.04 1.663.114 0-.637-.54-1.728-1.9-1.728-1.25 0-1.566.405-1.967.868ZM8.716 8.19c-2.04 0-2.304.87-2.304 1.416 0 .878 1.043 1.168 1.6 1.168 1.02 0 2.067-.282 2.232-2.423a6.2 6.2 0 0 0-1.528-.161" />
  </svg>
);

// InstagramIcon component
const InstagramIcon = (props) => (
  <svg
    className={props.className || "w-6 h-6"}
    fill="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.85s-.011 3.585-.069 4.85c-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07s-3.585-.012-4.85-.07c-3.252-.148-4.771-1.691-4.919-4.919-.058-1.265-.069-1.645-.069-4.85s.011-3.585.069-4.85c.149-3.225 1.664-4.771 4.919-4.919C8.416 2.175 8.796 2.163 12 2.163m0-2.163C8.74 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.74 0 12s.014 3.667.072 4.947c.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.74 24 12 24s3.667-.014 4.947-.072c4.358-.2 6.78-2.618 6.98-6.98C23.986 15.667 24 15.26 24 12s-.014-3.667-.072-4.947c-.2-4.358-2.618-6.78-6.98-6.98C15.667.014 15.26 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.88 1.44 1.44 0 000-2.88z" />
  </svg>
);
// GoogleIcon component
const GoogleIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height="24"
    viewBox="0 0 24 24"
    width="24"
  >
    <path
      d="M21.35,11.1H12.18V13.83H18.69C18.36,17.64 15.19,19.27 12.19,19.27C8.36,19.27 5,16.25 5,12C5,7.9 8.2,4.73 12.19,4.73C14.76,4.73 16.04,5.72 17.01,6.65L19.2,4.44C17.22,2.63 14.91,1.5 12.19,1.5C7.03,1.5 3,5.55 3,12C3,18.45 7.03,22.5 12.19,22.5C17.6,22.5 21.5,18.33 21.5,11.33C21.5,10.84 21.45,10.53 21.35,11.1Z"
      fill="currentColor"
    />
  </svg>
);
const ArrowLeftIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-gray-700"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);
const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-gray-700"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);
const EyeIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

// --- DYNAMIC IMAGE URL HELPER ---
const getImageUrl = (article, placeholderText = "News", version) => {
  if (article.image?.[0]) {
    let url = article.image[0];
    if (version) {
      // Append version as a cache-busting query parameter
      url += (url.includes("?") ? "&" : "?") + `v=${version}`;
    }
    return url;
  }
  return article.youtubeUrl
    ? `https://img.youtube.com/vi/${getYouTubeVideoId(
        article.youtubeUrl
      )}/hqdefault.jpg`
    : article.shortVideo
    ? `https://img.youtube.com/vi/${getYouTubeVideoId(
        article.shortVideo
      )}/hqdefault.jpg`
    : `https://placehold.co/800x450/666/fff?text=${placeholderText}`;
};

// --- PDF Thumbnail Component (Improved) ---
const PdfThumbnail = ({ pdfUrl }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const generateThumbnail = async () => {
      if (!window.pdfjsLib) {
        console.error("pdf.js library is not loaded.");
        setError("PDF library failed to load.");
        setIsLoading(false);
        return;
      }

      try {
        const loadingTask = window.pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const viewport = page.getViewport({ scale: 1 });
        const scale = 300 / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;

        await page.render({ canvasContext: context, viewport: scaledViewport })
          .promise;
        setThumbnailUrl(canvas.toDataURL("image/jpeg"));
      } catch (err) {
        console.error(
          "Failed to render PDF thumbnail for URL:",
          pdfUrl,
          "Error:",
          err
        );
        setError(
          "Could not load preview. This might be a CORS issue or an invalid PDF."
        );
        setThumbnailUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (pdfUrl) {
      generateThumbnail();
    } else {
      setIsLoading(false);
    }
  }, [pdfUrl]);

  if (isLoading) {
    return (
      <div className="w-full h-80 bg-gray-200 flex items-center justify-center">
        Loading Preview...
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-80 bg-red-100 flex flex-col items-center justify-center text-center p-4">
        <p className="font-semibold text-red-700">Preview Failed</p>
        <p className="text-xs text-red-600 mt-1">{error}</p>
      </div>
    );
  }

  if (!thumbnailUrl) {
    return (
      <div className="w-full h-80 bg-gray-300 flex items-center justify-center text-gray-500">
        Preview Not Available
      </div>
    );
  }

  return (
    <img
      src={thumbnailUrl}
      alt="E-Paper Thumbnail"
      className="w-full h-80 object-cover object-top"
    />
  );
};

// --- E-PAPER COMPONENTS ---

const EpaperModal = ({ pdfArticles, open, onClose, lang }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-black text-3xl z-20 leading-none"
        >
          &times;
        </button>
        <h2 className="text-xl font-bold mb-4 text-red-700 border-b pb-2 flex-shrink-0">
          E-Paper
        </h2>
        {pdfArticles && pdfArticles.length > 0 ? (
          <ul className="divide-y overflow-y-auto">
            {pdfArticles.map((article) => (
              <li
                key={article.id}
                className="py-3 flex justify-between items-center"
              >
                <span
                  className={`font-semibold text-blue-800 ${
                    lang === "gu" ? "font-noto-gujarati" : "font-sans"
                  }`}
                >
                  {formatArticleDate(article)}
                </span>
                <Link
                  to={`/epaper/${article.id}`}
                  onClick={onClose}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-gray-100"
                >
                  <EyeIcon />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4">No E-Papers available at the moment.</p>
        )}
      </div>
    </div>
  );
};

// --- E-Paper Page ---
const EpaperPage = ({ allArticles }) => {
  const { articleId } = useParams();
  const [filter, setFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  // debounce the live search input so we don't recompute too often
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const pdfArticles = useMemo(() => {
    return allArticles
      .filter((a) => a.pdf)
      .sort((a, b) => {
        const aDate = a.publishedDate
          ? new Date(a.publishedDate)
          : new Date(a.createdAt);
        const bDate = b.publishedDate
          ? new Date(b.publishedDate)
          : new Date(b.createdAt);
        return bDate - aDate;
      });
  }, [allArticles]);

  const articlesToShow = useMemo(() => {
    const now = new Date();
    let filteredArticles;

    switch (filter) {
      case "today":
        const today = now.toDateString();
        filteredArticles = pdfArticles.filter((article) => {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          return d.toDateString() === today;
        });
        break;
      case "7days":
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(now.getDate() - 7);
        filteredArticles = pdfArticles.filter((article) => {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          return d >= sevenDaysAgo;
        });
        break;
      case "15days":
        const fifteenDaysAgo = new Date();
        fifteenDaysAgo.setDate(now.getDate() - 15);
        filteredArticles = pdfArticles.filter((article) => {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          return d >= fifteenDaysAgo;
        });
        break;
      case "all":
      default:
        filteredArticles = pdfArticles;
    }

    // If a specific date is selected via date picker, filter by it (exact day match)
    if (selectedDate) {
      const parts = selectedDate.split("/");
      if (parts.length === 3) {
        const [dd, mm, yyyy] = parts.map((s) => parseInt(s, 10));
        const target = new Date(yyyy, mm - 1, dd).toDateString();
        filteredArticles = filteredArticles.filter((article) => {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          return d.toDateString() === target;
        });
      }
    }

    if (debouncedQuery !== "") {
      const q = debouncedQuery.toLowerCase();

      const tryParseDDMMYYYY = (s) => {
        const parts = String(s).split("/");
        if (parts.length === 3) {
          const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
          if (!isNaN(dd) && !isNaN(mm) && !isNaN(yyyy))
            return new Date(yyyy, mm - 1, dd).toDateString();
        }
        return null;
      };

      const targetDateString = tryParseDDMMYYYY(q);

      return filteredArticles.filter((article) => {
        // 1) exact date match if query is dd/mm/yyyy
        if (targetDateString) {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          if (d.toDateString() === targetDateString) return true;
          if (article.date) {
            // compare dd/mm/yyyy raw
            if (String(article.date).toLowerCase().includes(q)) return true;
          }
        }

        // 2) match formatted date (e.g., 'October 8, 2025')
        if (formatArticleDate(article).toLowerCase().includes(q)) return true;

        // 3) match by title
        if (article.title && String(article.title).toLowerCase().includes(q))
          return true;

        // 4) match by pdf filename or url
        if (article.pdf && String(article.pdf).toLowerCase().includes(q))
          return true;

        // 5) numeric queries may correspond to year/day
        if (/^\d{4}$/.test(q)) {
          const year = q;
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          if (String(d.getFullYear()) === year) return true;
        }

        return false;
      });
    }

    return filteredArticles;
  }, [filter, pdfArticles, debouncedQuery, selectedDate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  if (articleId) {
    const pdfArticle = allArticles.find((a) => a.id === parseInt(articleId));
    if (!pdfArticle || !pdfArticle.pdf) {
      return (
        <main className="flex-grow container mx-auto px-4 py-8 text-center">
          <MetaTags
            title="E-Paper Not Found"
            description="The requested e-paper could not be found or is not available."
          />
          <h1 className="text-3xl font-bold mb-6">E-Paper Not Found</h1>
          <p>The requested E-Paper could not be found or is not available.</p>
        </main>
      );
    }
    return (
      <main className="flex-grow container mx-auto px-4 py-8">
        <MetaTags
          title={`E-Paper: ${formatArticleDate(pdfArticle)}`}
          description={`Read the full e-paper from ${formatArticleDate(
            pdfArticle
          )}. Amdavad Post digital newspaper.`}
        />
        <h1 className="text-3xl font-bold mb-4 capitalize border-b-2 border-red-600 pb-2">
          E-Paper: {formatArticleDate(pdfArticle)}
        </h1>
        <div className="w-full h-[80vh] md:h-[100vh]">
          <iframe
            src={pdfArticle.pdf}
            title={`E-Paper for ${formatArticleDate(pdfArticle)}`}
            className="w-full h-full border-none"
          ></iframe>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <MetaTags
        title="E-Paper Archives"
        description="Browse and read the archives of Amdavad Post e-papers. Access past editions of our newspaper."
      />
      <div className="flex flex-col md:flex-row justify-between items-center border-b-2 border-red-600 pb-2 mb-6 gap-4">
        <h1 className="text-3xl font-bold capitalize">E-Paper</h1>
        <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
          <div className="relative w-full sm:w-auto">
            <input
              type="text"
              placeholder="Live search: title, date (dd/mm/yyyy), or filename"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="p-2 border rounded-md pl-8 w-full"
            />
            <svg
              className="w-5 h-5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="filter-select" className="font-semibold">
              Show:
            </label>
            <select
              id="filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="p-2 border rounded-md"
            >
              <option value="all">All</option>
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="15days">Last 15 Days</option>
            </select>
            <div className="flex items-center space-x-2 ml-4">
              <SmallDatePicker
                value={selectedDate}
                onChange={(v) => {
                  setSelectedDate(v);
                  setFilter("all");
                }}
              />
              {selectedDate && (
                <button
                  type="button"
                  className="text-sm text-gray-500 hover:text-gray-800"
                  onClick={() => setSelectedDate("")}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {articlesToShow.length > 0 ? (
          articlesToShow.map((article) => (
            <div
              key={article.id}
              className="group block bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 relative"
            >
              <div className="absolute top-2 right-2 z-20">
                <ShareButton
                  url={`${window.location.origin}/epaper/${article.id}`}
                  title={formatArticleDate(article)}
                />
              </div>
              <Link to={`/epaper/${article.id}`} className="block">
                <PdfThumbnail pdfUrl={article.pdf} />
                <div className="p-4">
                  <p className="font-bold text-lg">
                    {formatArticleDate(article)}
                  </p>
                  <span className="text-sm text-blue-600 group-hover:underline">
                    View E-Paper
                  </span>
                </div>
              </Link>
            </div>
          ))
        ) : (
          <p className="col-span-full text-center py-8">
            No E-Papers found for the selected criteria.
          </p>
        )}
      </div>
    </main>
  );
};

// Helper function to safely parse article categories
const parseCategories = (categories) => {
  if (Array.isArray(categories)) {
    // Categories is already an array (expected format from DataTypes.JSON)
    return categories;
  } else if (typeof categories === "string") {
    try {
      // Try to parse as JSON array string like '["worldnews", "sports"]'
      return JSON.parse(categories);
    } catch (e) {
      // If JSON.parse fails, handle as plain string
      if (categories.includes(",")) {
        // It's like "worldnews,business", so split it into an array
        return categories.split(",").map((cat) => cat.trim());
      } else {
        // It's a single plain string like "worldnews", so wrap it in an array
        return [categories];
      }
    }
  }
  return [];
};

// --- NEW PAGE COMPONENTS ---

const CategoryPage = ({ allArticles, lang, textData, imageVersion }) => {
  const { categoryName } = useParams();
  const navigate = useNavigate();

  const displayTitle = useMemo(() => {
    const navLinks = textData.nav;
    const foundItem = navLinks.find((item) => {
      const lowerItem = item.toLowerCase();
      if (lowerItem === "ahmedabad" && categoryName === "localnews")
        return true;
      if (lowerItem === "sports" && categoryName === "sportsnews") return true;
      if (lowerItem === "lifestyle" && categoryName === "other") return true;
      return lowerItem === categoryName;
    });
    return foundItem || categoryName;
  }, [categoryName, textData.nav]);

  const articlesForCategory = allArticles.filter((article) => {
    // Gracefully handle missing or empty categories data
    if (!article.categories) {
      return false;
    }

    // Use helper function to safely parse categories
    const categories = parseCategories(article.categories);

    // Ensure 'categories' is always an array and convert to lowercase
    const lowerCaseCategories = categories.map((c) => String(c).toLowerCase());

    // Now, perform the filtering logic safely
    const lowerCaseCategoryName = categoryName.toLowerCase();

    // Special handling for "world" category to match "worldnews"
    if (lowerCaseCategoryName === "world") {
      return lowerCaseCategories.includes("worldnews");
    }

    // For all other categories, direct inclusion check
    return lowerCaseCategories.includes(lowerCaseCategoryName);
  });
  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <MetaTags
        title={`${displayTitle} News`}
        description={`Latest news and updates from the ${displayTitle} category. Stay informed with Amdavad Post.`}
      />
      <h1 className="text-3xl font-bold mb-6 capitalize border-b-2 border-red-600 pb-2">
        {displayTitle} News
      </h1>
      {articlesForCategory.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articlesForCategory.map((news) => (
            <div
              key={news.id}
              className="bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 relative"
            >
              <div className="absolute top-2 right-2 z-20">
                <ShareButton
                  url={`${window.location.origin}/news/${news.slug}`}
                  title={news.title}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    navigate(`/news/${news.slug}`);
                }}
                onClick={() => navigate(`/news/${news.slug}`)}
                className="cursor-pointer"
              >
                <img
                  src={getImageUrl(news, categoryName, imageVersion)}
                  alt={news.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <div className="mb-2 flex flex-wrap gap-2">
                    {(parseCategories(news.categories) || []).map((c, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full capitalize"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  <h3
                    className={`font-bold text-lg mb-2 h-20 overflow-hidden ${
                      lang === "gu" ? "font-noto-gujarati" : "font-sans"
                    }`}
                  >
                    {news.title}
                  </h3>
                  <div className="text-sm text-gray-500 flex justify-between items-center mt-4">
                    <span>{formatDate(news.createdAt)}</span>
                    <span className="font-semibold text-red-700 hover:text-red-900">
                      Read More &rarr;
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p>No articles found in this category.</p>
      )}
    </main>
  );
};

const SingleNewsPage = ({ allArticles, lang, textData, imageVersion }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = allArticles.find((a) => a.slug === slug);
  // Ensure images is always defined
  const images =
    article &&
    article.image &&
    Array.isArray(article.image) &&
    article.image.length > 0
      ? article.image.map((img) => `${img}?v=${imageVersion}`)
      : [getImageUrl(article, "News", imageVersion)];
  const hasMultipleImages =
    article &&
    article.image &&
    Array.isArray(article.image) &&
    images.length > 1;

  const currentCategory = useMemo(() => {
    if (!article || !article.categories) return null;
    const categories = parseCategories(article.categories);
    return categories[0] || null;
  }, [article]);

  const relatedArticles = useMemo(() => {
    if (!currentCategory || !article) return [];
    return allArticles.filter((a) => {
      if (a.id === article.id) return false;
      const aCategories = parseCategories(a.categories);
      return aCategories.includes(currentCategory);
    });
  }, [allArticles, article, currentCategory]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="flex-grow flex justify-center items-center h-screen text-red-500">
        <MetaTags
          title="Article Not Found"
          description="The article you are looking for does not exist or has been moved."
        />
        Article not found.
      </div>
    );
  }

  const metaDescription = article.content
    ? article.content.substring(0, 160) + "..."
    : "Read more at Amdavad Post.";

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <MetaTags title={article.title} description={metaDescription} />
      <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-4">
          <h1
            className={`text-3xl md:text-4xl font-bold ${
              lang === "gu" ? "font-noto-gujarati" : "font-sans"
            }`}
          >
            {article.title}
          </h1>
          <div className="ml-4 flex-shrink-0">
            <ShareButton
              url={`${window.location.origin}/news/${article.slug}`}
              title={article.title}
            />
          </div>
        </div>
        <div className="flex items-center space-x-4 mb-6 text-sm">
          <div className="flex flex-wrap gap-2">
            {(
              parseCategories(article.categories) || [currentCategory || "News"]
            ).map((c, idx) => (
              <span
                key={idx}
                className="font-semibold text-red-600 bg-red-100 px-2 py-1 rounded-full self-start inline-block capitalize"
              >
                {c}
              </span>
            ))}
          </div>
          <p className="text-gray-500">{formatDate(article.createdAt)}</p>
        </div>
        {/* Gallery if multiple images, else single image as before */}
        {hasMultipleImages ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-8">
            {images.map((img, idx) => (
              <button
                key={idx}
                className="focus:outline-none"
                onClick={() => {
                  setModalOpen(true);
                  setModalIdx(idx);
                }}
              >
                <img
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition"
                />
              </button>
            ))}
          </div>
        ) : (
          <img
            src={images[0]}
            alt={article.title}
            className="w-full h-auto max-h-[500px] object-cover rounded-lg mb-8"
          />
        )}
        {/* Modal for full gallery view, close btn top-right outside image */}
        {modalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
            onClick={() => setModalOpen(false)}
          >
            <button
              className="absolute top-6 right-8 text-white text-4xl font-bold bg-black/60 rounded-full px-3 py-1 z-50 hover:bg-black/80 focus:outline-none"
              style={{ marginTop: "0" }}
              onClick={() => setModalOpen(false)}
            >
              &times;
            </button>
            <div
              className="relative w-full max-w-2xl p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[modalIdx]}
                alt={`Gallery ${modalIdx + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}

        <div
          className={`text-gray-800 text-lg leading-relaxed mb-8 ${
            lang === "gu" ? "font-noto-gujarati" : "font-sans"
          }`}
        >
          {/*
            Render stored HTML content. If content already contains HTML tags,
            render as-is. Otherwise, convert simple *star* markup to <strong>
            and preserve newlines as <br />. We escape before conversion to
            reduce injection risk for plain text content.
          */}
          <div
            dangerouslySetInnerHTML={{
              __html: (function () {
                const raw = article.content || "";
                if (/<[^>]+>/g.test(raw)) return raw;
                // escape basic HTML entities
                const escaped = String(raw)
                  .replace(/&/g, "&amp;")
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;")
                  .replace(/"/g, "&quot;")
                  .replace(/'/g, "&#039;");
                // convert *star* markup to <strong>
                const converted = escaped.replace(
                  /\*(.+?)\*/g,
                  (m, p1) => `<strong>${p1}</strong>`
                );
                return converted.replace(/\r?\n/g, "<br />");
              })(),
            }}
          />
        </div>

        {/* --- ADDED REFERENCE URL BLOCK --- */}
        {article.reference_url && (
          <div className="mt-6 mb-8 text-sm text-gray-600">
            <span className="font-bold text-gray-800">Reference: </span>
            <a
              href={article.reference_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline break-all"
            >
              {article.reference_url}
            </a>
          </div>
        )}
        {/* --- END OF BLOCK --- */}
        {/* --- ADDED SOCIAL MEDIA POST URL BLOCK --- */}
        {article.embedUrl && (
          <div className="mt-6 mb-8 text-sm text-gray-600">
            <span className="font-bold text-gray-800">
              {
                /* Check the URL and set the label text accordingly */
                article.embedUrl.toLowerCase().includes("x.com") ||
                article.embedUrl.toLowerCase().includes("twitter.com")
                  ? "X POST : "
                  : article.embedUrl.toLowerCase().includes("instagram.com")
                  ? "INSTAGRAM POST : "
                  : article.embedUrl.toLowerCase().includes("facebook.com")
                  ? "FACEBOOK POST : "
                  : "Embed URL: " /* Default fallback */
              }{" "}
            </span>
            <a
              href={article.embedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline break-all"
            >
              {article.embedUrl}
            </a>
          </div>
        )}
       {/* --- Embeddable Content --- */}
{/* Check if the article object exists AND if it has any
  of the possible embed URL fields.
*/}
{article && (article.embedUrl || article.embedURL || article.embed_url) && (
  <div className="mt-6">
    <Embed
      url={article.embedUrl || article.embedURL || article.embed_url}
      className="w-full"
    />
  </div>
)}
        {/* --- END OF BLOCK --- */}

        {article.youtubeUrl && (
          <div className="my-8 aspect-video">
            <iframe
              className="w-full h-full rounded-lg"
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                article.youtubeUrl
              )}?autoplay=0&controls=1`}
              title={article.title}
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}

        {article.shortVideo && (
          <div className="my-8 mx-auto max-w-sm">
            <div className="aspect-[9/16]">
              <iframe
                className="w-full h-full rounded-lg"
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                  article.shortVideo
                )}?autoplay=0`}
                title={article.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}
      </div>

      {relatedArticles.length > 0 && (
        <div className="mt-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-4 border-b-2 border-red-600 pb-2">
            {textData.headings.relatedNews}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedArticles.map((news) => (
              <div
                key={news.id}
                className="bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="relative">
                  <div className="absolute top-2 right-2 z-20">
                    <ShareButton
                      url={`${window.location.origin}/news/${news.slug}`}
                      title={news.title}
                    />
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        navigate(`/news/${news.slug}`);
                    }}
                    onClick={() => navigate(`/news/${news.slug}`)}
                    className="cursor-pointer"
                  >
                    <img
                      src={getImageUrl(news, "News", imageVersion)}
                      alt={news.title}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-3">
                      <div className="mb-2 flex flex-wrap gap-2">
                        {(parseCategories(news.categories) || []).map(
                          (c, idx) => (
                            <span
                              key={idx}
                              className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full capitalize"
                            >
                              {c}
                            </span>
                          )
                        )}
                      </div>
                      <h4
                        className={`font-bold text-md mb-1 h-16 overflow-hidden ${
                          lang === "gu" ? "font-noto-gujarati" : "font-sans"
                        }`}
                      >
                        {news.title}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {formatDate(news.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
};

const VideosPage = ({ allArticles, lang }) => {
  const videoArticles = allArticles.filter((a) => a.youtubeUrl);
  const [currentVideo, setCurrentVideo] = useState(videoArticles[0]);
  const [hasInteracted, setHasInteracted] = useState(false);

  const currentVideoId = useMemo(
    () => (currentVideo ? getYouTubeVideoId(currentVideo.youtubeUrl) : null),
    [currentVideo]
  );

  const handleVideoSelect = (video) => {
    setCurrentVideo(video);
    setHasInteracted(true);
  };

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <MetaTags
        title="Videos"
        description="Watch the latest news videos, interviews, and special reports from Amdavad Post."
      />
      <h1 className="text-3xl font-bold mb-6 capitalize border-b-2 border-red-600 pb-2">
        Videos
      </h1>
      {currentVideoId && (
        <div className="relative bg-black rounded-lg overflow-hidden mb-6 h-96 md:h-[500px]">
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${currentVideoId}?autoplay=${
              hasInteracted ? 1 : 0
            }&controls=1`}
            title={currentVideo.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {videoArticles.map((video) => (
          <div
            key={video.id}
            onClick={() => handleVideoSelect(video)}
            className="group relative rounded-lg overflow-hidden cursor-pointer"
          >
            <div className="absolute top-2 right-2 z-20">
              <ShareButton
                url={`${window.location.origin}/news/${video.slug || video.id}`}
                title={video.title}
              />
            </div>
            <img
              src={`https://img.youtube.com/vi/${getYouTubeVideoId(
                video.youtubeUrl
              )}/mqdefault.jpg`}
              alt={video.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <PlayIcon />
            </div>
            <div className="absolute bottom-0 left-0 p-2 bg-gradient-to-t from-black/80 to-transparent w-full">
              <p
                className={`text-white text-sm font-semibold leading-tight h-10 overflow-hidden ${
                  lang === "gu" ? "font-noto-gujarati" : "font-sans"
                }`}
              >
                {video.title}
              </p>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
};

const ShortsPage = ({ allArticles, lang }) => {
  const shortArticles = allArticles.filter((a) => a.shortVideo);
  const [selectedReel, setSelectedReel] = useState(null);

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <MetaTags
        title="Shorts"
        description="Watch short news clips, reels, and quick updates from Amdavad Post."
      />
      <h1 className="text-3xl font-bold mb-6 capitalize border-b-2 border-red-600 pb-2">
        Shorts
      </h1>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {shortArticles.map((reel) => (
          <div
            key={reel.id}
            className="group cursor-pointer"
            onClick={() => setSelectedReel(reel)}
          >
            <div className="relative rounded-lg overflow-hidden aspect-[9/16] bg-gray-700">
              <div className="absolute top-2 right-2 z-20">
                <ShareButton
                  url={`${window.location.origin}/news/${reel.slug || reel.id}`}
                  title={reel.title}
                />
              </div>
              <img
                src={`https://img.youtube.com/vi/${getYouTubeVideoId(
                  reel.shortVideo
                )}/mqdefault.jpg`}
                alt={reel.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <PlayIcon />
              </div>
            </div>
            <h4
              className={`mt-2 font-semibold text-sm h-10 ${
                lang === "gu" ? "font-noto-gujarati" : "font-sans"
              }`}
            >
              {reel.title}
            </h4>
          </div>
        ))}
      </div>
      <ReelPlayerModal
        reel={selectedReel}
        onClose={() => setSelectedReel(null)}
        lang={lang}
      />
    </main>
  );
};

// --- CORE COMPONENTS (Modified) ---

const Header = ({ lang, setLang, textData, ticker }) => {
  const location = useLocation();
  const [navOpen, setNavOpen] = React.useState(false);
  // Close nav when other parts of the app request it (footer logo, etc.)
  React.useEffect(() => {
    const handler = () => setNavOpen(false);
    window.addEventListener("closeNav", handler);
    return () => window.removeEventListener("closeNav", handler);
  }, []);
  const today = new Date();
  const dateString = today.toLocaleDateString(
    lang === "gu" ? "gu-IN" : "en-US",
    { weekday: "long", day: "numeric", month: "long", year: "numeric" }
  );
  const timeString = today.toLocaleTimeString(
    lang === "gu" ? "gu-IN" : "en-US",
    { hour: "2-digit", minute: "2-digit", hour12: true }
  );

  const getLinkForItem = (item) => {
    const lowerItem = item.toLowerCase();
    if (lowerItem === "home") return "/";
    if (lowerItem === "videos") return "/videos";
    if (lowerItem === "shorts") return "/shorts";
    if (lowerItem === "ahmedabad") return "/category/localnews";
    if (lowerItem === "sports") return "/category/sportsnews";
    if (lowerItem === "lifestyle") return "/category/other";
    return `/category/${lowerItem}`;
  };

  return (
    <header className="bg-white shadow-md">
      <div className="bg-gray-800 text-white text-xs sm:text-sm py-1">
        <div className="container mx-auto px-2 sm:px-4 flex flex-col sm:flex-row justify-between items-center gap-2">
          <span>{dateString}</span>
          <span>Ahmedabad: ({timeString})</span>
        </div>
      </div>
      <div className="container mx-auto px-2 sm:px-4 py-3">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 w-full">
          <div className="flex-shrink-0 w-32 h-16">
            <Link
              to="/"
              onClick={() => {
                // Close mobile nav if open and scroll to top
                setNavOpen(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <img
                src="/logo.jpg"
                alt="Logo"
                className="w-full h-full object-cover rounded-lg cursor-pointer"
              />
            </Link>
          </div>
          <div className="bg-[#ffbb00] border border-yellow-300 rounded-full px-4 py-2 flex-grow overflow-hidden flex items-center space-x-2 text-sm text-gray-800 w-full">
            <span className="bg-red-700 w-2 h-2 rounded-full animate-pulse flex-shrink-0"></span>
            <div className="w-full">
              <Marquee text={ticker.join("   •   ")} pixelsPerSecond={90} />
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-2">
            <Link to="/epaper">
              <button className="bg-[#ffbb00] text-gray-900 font-semibold py-2 rounded-lg flex items-center justify-center hover:bg-yellow-200 transition duration-300 w-40">
                <EpaperIcon />
                {textData.buttons.epaper}
              </button>
            </Link>
          </div>
        </div>
      </div>
      <nav className="bg-red-700 text-white relative">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="md:hidden">
            <button
              onClick={() => setNavOpen(!navOpen)}
              className="p-2 focus:outline-none"
            >
              <svg
                className="w-7 h-7"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>

          <ul className="hidden md:flex flex-wrap justify-center md:justify-start space-x-2 font-semibold">
            {textData.nav.map((item) => {
              const linkPath = getLinkForItem(item);
              // Never highlight 'Home' as active
              const isActive =
                item.toLowerCase() !== "home" && location.pathname === linkPath;
              return (
                <li key={item}>
                  <Link
                    to={linkPath}
                    className={`block py-3 px-3 rounded-md transition-colors duration-200 ${
                      isActive
                        ? "bg-red-900" // Active style
                        : "hover:bg-red-800" // Hover style
                    }`}
                  >
                    {item}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="md:hidden">
            <Link to="/epaper">
              <button className="bg-[#ffbb00] text-gray-900 font-semibold py-1 px-3 rounded-lg flex items-center justify-center hover:bg-yellow-200 transition duration-300 text-sm">
                <EpaperIcon />
                {textData.buttons.epaper}
              </button>
            </Link>
          </div>
        </div>
        {navOpen && (
          <div className="md:hidden absolute left-0 top-full bg-white text-gray-900 rounded-b-lg shadow-lg z-50 w-full">
            <ul>
              {textData.nav.map((item) => {
                const linkPath = getLinkForItem(item);
                const isActive = location.pathname === linkPath;
                return (
                  <li
                    key={item}
                    className={`border-b last:border-b-0 ${
                      isActive ? "bg-red-50" : ""
                    }`}
                  >
                    <Link
                      to={linkPath}
                      className={`block w-full py-3 px-4 transition-colors duration-200 ${
                        isActive
                          ? "text-red-700 font-semibold"
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() => setNavOpen(false)}
                    >
                      {item}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
};
const HeroSection = ({ article, textData, lang, imageVersion }) => {
  // Initialize the navigate function
  const navigate = useNavigate();

  if (!article)
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        No featured article available.
      </div>
    );

  let category = "News"; // Default category
  if (article.categories) {
    const parsedCategories = parseCategories(article.categories);
    category = parsedCategories.length > 0 ? parsedCategories[0] : "News";
  }

  const displayCategory =
    String(category).charAt(0).toUpperCase() + String(category).slice(1);

  const rawContent = article.content || "";
  // If content contains HTML, truncate while preserving tags so
  // existing markup like <strong> remains active in the hero preview.
  let truncatedContent;
  if (/<[^>]+>/g.test(rawContent)) {
    truncatedContent = truncateHtml(rawContent, 250);
  } else {
    const previewRaw =
      rawContent.length > 250
        ? rawContent.substring(0, 250) + "..."
        : rawContent;
    truncatedContent = convertStarsToHtml(previewRaw);
  }

  const images =
    article.image && article.image.length > 0
      ? article.image.map((img) => `${img}?v=${imageVersion}`)
      : [getImageUrl(article, "News", imageVersion)];

  // --- HANDLERS ---
  // 1. Navigate to the article page
  const handleCardClick = () => {
    navigate(`/news/${article.slug}`);
  };

  // 2. Stop the click event from bubbling up to the card
  const handleShareClick = (e) => {
    e.stopPropagation();
    // The ShareButton component will handle its own click logic
  };

  return (
    <div className="container mx-auto px-4 py-4">
      {/* - Added onClick handler to the main card
        - Added cursor-pointer for visual feedback
        - Added 'group' for 'group-hover' effects on children
      */}
      <div
        className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col md:flex-row relative cursor-pointer group"
        onClick={handleCardClick}
      >
        <div className="md:w-1/2">
          {(() => {
            const cnt = images ? images.length : 0;
            // --- No changes to the image logic below ---
            if (cnt <= 1) {
              return (
                <div className="w-full h-64 md:h-[420px] overflow-hidden rounded-lg">
                  <img
                    src={images[0]}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              );
            }
            if (cnt === 2) {
              return (
                <div className="grid grid-cols-2 gap-2">
                  <div className="h-64 md:h-[420px] overflow-hidden rounded-lg">
                    <img
                      src={images[0]}
                      alt={`${article.title} - 1`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="h-64 md:h-[420px] overflow-hidden rounded-lg">
                    <img
                      src={images[1]}
                      alt={`${article.title} - 2`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              );
            }
            if (cnt === 3) {
              return (
                <div className="flex flex-col md:flex-row gap-2">
                  <div className="md:w-2/3 h-64 md:h-[420px] overflow-hidden rounded-lg">
                    <img
                      src={images[0]}
                      alt={`${article.title} - 1`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="md:w-1/3 flex flex-col gap-2">
                    <div className="h-32 md:h-[209px] overflow-hidden rounded-lg">
                      <img
                        src={images[1]}
                        alt={`${article.title} - 2`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="h-32 md:h-[209px] overflow-hidden rounded-lg">
                      <img
                        src={images[2]}
                        alt={`${article.title} - 3`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              );
            }
            if (cnt === 4) {
              return (
                <div className="flex flex-col gap-2">
                  <div className="w-full h-64 md:h-[420px] overflow-hidden rounded-lg">
                    <img
                      src={images[0]}
                      alt={`${article.title} - 1`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {images.slice(1, 4).map((img, idx) => (
                      <div
                        key={idx}
                        className="h-24 md:h-28 overflow-hidden rounded-lg"
                      >
                        <img
                          src={img}
                          alt={`${article.title} - ${idx + 2}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            if (cnt === 5) {
              return (
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-48 md:h-[280px] overflow-hidden rounded-lg">
                      <img
                        src={images[0]}
                        alt={`${article.title} - 1`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="h-48 md:h-[280px] overflow-hidden rounded-lg">
                      <img
                        src={images[1]}
                        alt={`${article.title} - 2`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {images.slice(2, 5).map((img, idx) => (
                      <div
                        key={idx}
                        className="h-24 md:h-[140px] overflow-hidden rounded-lg"
                      >
                        <img
                          src={img}
                          alt={`${article.title} - ${idx + 3}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <div className="flex flex-col gap-2">
                <div className="w-full h-64 md:h-[420px] overflow-hidden rounded-lg">
                  <img
                    src={images[0]}
                    alt={`${article.title} - 1`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {images.slice(1, 5).map((img, idx) => (
                    <div
                      key={idx}
                      className="h-24 md:h-28 overflow-hidden rounded-lg"
                    >
                      <img
                        src={img}
                        alt={`${article.title} - ${idx + 2}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
            // --- End of image logic ---
          })()}
        </div>

        {/* - Added onClick handler to the ShareButton's wrapper
         */}
        <div className="absolute top-2 right-2 z-20" onClick={handleShareClick}>
          <ShareButton
            url={`${window.location.origin}/news/${article.slug}`}
            title={article.title}
          />
        </div>

        <div className="md:w-1/2 p-6 flex flex-col justify-center">
          <div className="mb-2 flex flex-wrap gap-2">
            {(parseCategories(article.categories) || [displayCategory]).map(
              (c, i) => (
                <span
                  key={i}
                  className="text-sm font-semibold text-red-600 bg-red-100 px-2 py-1 rounded-full self-start capitalize"
                >
                  {c}
                </span>
              )
            )}
          </div>
          <h2
            className={`text-2xl md:text-3xl font-bold mb-3 ${
              lang === "gu" ? "font-noto-gujarati" : "font-sans"
            }`}
          >
            {article.title}
          </h2>
          <p
            className={`text-gray-600 mb-4 ${
              lang === "gu" ? "font-noto-gujarati" : "font-sans"
            }`}
            dangerouslySetInnerHTML={{ __html: truncatedContent }}
          />
          <div className="text-sm text-gray-500 flex justify-between items-center">
            <span>{formatDate(article.createdAt)}</span>

            {/* - Replaced <Link> with <span>
              - Added 'group-hover' to make it respond to the card's hover
            */}
            <span className="font-bold text-red-700 group-hover:text-red-900 transition-colors">
              {textData.readMore} &rarr;
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const VideosSection = ({ videos, headings, lang }) => {
  // --- Call ALL hooks at the top ---
  const [currentVideo, setCurrentVideo] = useState(videos[0]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const scrollContainerRef = useRef(null);

  const currentVideoId = useMemo(
    () => (currentVideo ? getYouTubeVideoId(currentVideo.youtubeUrl) : null),
    [currentVideo]
  );

  // --- Now you can have your conditional return ---
  if (!videos || videos.length === 0) {
    return null;
  }

  // --- The rest of your component logic ---
  const handleVideoSelect = (video) => {
    setCurrentVideo(video);
    setHasInteracted(true);
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.offsetWidth * 0.8;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-4">
      <h2 className="text-2xl font-bold mb-4 border-b-2 border-red-600 pb-2">
        {headings.videos}
      </h2>
      {currentVideoId && (
        <div className="relative bg-black rounded-lg overflow-hidden mb-6 h-96 md:h-[500px]">
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${currentVideoId}?autoplay=${
              hasInteracted ? 1 : 0
            }&controls=1`}
            title={currentVideo.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      )}
      <div className="relative">
        <button
          onClick={() => scroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-transform transform hover:scale-110 -ml-4"
        >
          <ArrowLeftIcon />
        </button>
        <div
          ref={scrollContainerRef}
          className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide"
        >
          {videos.map((video) => {
            const videoId = getYouTubeVideoId(video.youtubeUrl);
            if (!videoId) return null;
            return (
              <div
                key={video.id}
                onClick={() => handleVideoSelect(video)}
                className="group relative rounded-lg overflow-hidden cursor-pointer flex-shrink-0 w-64"
              >
                <div className="absolute top-2 right-2 z-20">
                  <ShareButton
                    url={`${window.location.origin}/news/${
                      video.slug || video.id
                    }`}
                    title={video.title}
                  />
                </div>
                <img
                  src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <PlayIcon />
                </div>
                <div className="absolute bottom-0 left-0 p-2 bg-gradient-to-t from-black/80 to-transparent w-full">
                  <p
                    className={`text-white text-sm font-semibold leading-tight h-10 overflow-hidden ${
                      lang === "gu" ? "font-noto-gujarati" : "font-sans"
                    }`}
                  >
                    {video.title}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <button
          onClick={() => scroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-transform transform hover:scale-110 -mr-4"
        >
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
};

const ReelPlayerModal = ({ reel, onClose, lang }) => {
  if (!reel) return null;
  const videoId = getYouTubeVideoId(reel.shortVideo);
  if (!videoId) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-lg overflow-hidden w-full max-w-4xl flex flex-col md:flex-row max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-black text-3xl z-20 leading-none"
        >
          &times;
        </button>
        {/* Left side: Video Player */}
        <div className="w-full md:w-1/3 flex-shrink-0 bg-black">
          <div className="aspect-[9/16] w-full">
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=0`}
              title={reel.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
        {/* Right side: Title and Content */}
        <div className="w-full md:w-2/3 p-6 overflow-y-auto">
          <h2
            className={`text-2xl font-bold mb-3 ${
              lang === "gu" ? "font-noto-gujarati" : "font-sans"
            }`}
          >
            {reel.title}
          </h2>
          <div
            className={`text-gray-700 whitespace-pre-wrap ${
              lang === "gu" ? "font-noto-gujarati" : "font-sans"
            }`}
            dangerouslySetInnerHTML={{
              __html: (function () {
                const raw = reel.content || "";
                // If content already contains HTML tags, assume it's saved as HTML and render as-is.
                if (/<[^>]+>/g.test(raw)) return raw;
                // Otherwise, convert star-markup *bold* into <strong>
                return convertStarsToHtml(raw);
              })(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

const ReelsSection = ({ reels, headings, lang }) => {
  const [selectedReel, setSelectedReel] = useState(null);
  const scrollContainerRef = useRef(null);

  if (!reels || reels.length === 0) return null;

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.offsetWidth * 0.8;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="container mx-auto px-4 pt-4 pb-8">
      <h2 className="text-2xl font-bold mb-4 border-b-2 border-red-600 pb-2">
        {headings.reels}
      </h2>
      <div className="relative">
        <button
          onClick={() => scroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-transform transform hover:scale-110 -ml-4"
        >
          <ArrowLeftIcon />
        </button>
        <div
          ref={scrollContainerRef}
          className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide"
        >
          {reels.map((reel) => {
            const videoId = getYouTubeVideoId(reel.shortVideo);
            if (!videoId) return null;
            return (
              <div
                key={reel.id}
                className="flex-shrink-0 w-48 group cursor-pointer"
                onClick={() => setSelectedReel(reel)}
              >
                <div className="relative rounded-lg overflow-hidden h-72 bg-gray-700">
                  <div className="absolute top-2 right-2 z-20">
                    <ShareButton
                      url={`${window.location.origin}/news/${
                        reel.slug || reel.id
                      }`}
                      title={reel.title}
                    />
                  </div>
                  <img
                    src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                    alt={reel.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40"></div>
                  <div className="absolute top-2 left-2 text-white font-bold text-sm bg-black/50 px-2 py-1 rounded">
                    Shorts
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PlayIcon />
                  </div>
                </div>
                <h4
                  className={`mt-2 font-semibold text-sm h-10 ${
                    lang === "gu" ? "font-noto-gujarati" : "font-sans"
                  }`}
                >
                  {reel.title}
                </h4>
              </div>
            );
          })}
        </div>
        <button
          onClick={() => scroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-transform transform hover:scale-110 -mr-4"
        >
          <ArrowRightIcon />
        </button>
      </div>
      <ReelPlayerModal
        reel={selectedReel}
        onClose={() => setSelectedReel(null)}
        lang={lang}
      />
    </div>
  );
};

// --- FOOTER AND MODAL ---
const LocationIcon = () => (
  <svg
    className="w-10 h-10 mr-2 text-red-600"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 11.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 2C7.03 2 3 6.03 3 11c0 5.25 7.5 11 9 11s9-5.75 9-11c0-4.97-4.03-9-9-9z"
    />
  </svg>
);
const PhoneIcon = () => (
  <svg
    className="w-5 h-5 mr-2 text-green-600"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm0 14a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5a2 2 0 00-2 2v2zm14-14a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5a2 2 0 012-2h2zm0 14a2 2 0 002 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2a2 2 0 012-2h2z"
    />
  </svg>
);
const MailIcon = () => (
  <svg
    className="w-5 h-5 mr-2 text-blue-600"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const PrivacyPolicyModal = ({ open, onClose, heading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 overflow-y-auto relative"
        style={{ maxHeight: "70vh", minHeight: "320px" }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-2xl font-bold"
          aria-label="Close"
        >
          &times;
        </button>
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "60vh", scrollbarWidth: "none" }}
        >
          <h2 className="text-xl font-bold mb-4 text-red-700 flex items-center">
            <svg
              className="w-6 h-6 mr-2 text-red-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 11.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 2C7.03 2 3 6.03 3 11c0 5.25 7.5 11 9 11s9-5.75 9-11c0-4.97-4.03-9-9-9z"
              />
            </svg>
            {heading}
          </h2>
          {heading === "Privacy Policy" && (
            <>
              {" "}
              <p className="mb-4 text-gray-700 text-sm">
                At Amdavad Post, we respect your privacy and are committed to
                protecting your personal information. This Privacy Policy
                outlines our practices concerning the collection, use, and
                safeguarding of data belonging to our readers, subscribers, and
                website visitors.
              </p>
              <ol className="space-y-3 text-gray-700 text-sm">
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 01-8 0M12 3v4m0 0a4 4 0 01-4 4m4-4a4 4 0 014 4m-4 4v4m0 0a4 4 0 01-4 4m4-4a4 4 0 014 4"
                      />
                    </svg>
                    Types of Information We Collect
                  </span>
                  <ul className="list-disc ml-7">
                    <li>
                      <span className="font-semibold">
                        Personal Information:
                      </span>{" "}
                      We collect details you voluntarily provide (such as name,
                      email address, and phone number) when you contact us,
                      subscribe to our publications, or place an advertisement.
                    </li>
                    <li>
                      <span className="font-semibold">
                        Non-Personal Information:
                      </span>{" "}
                      We may automatically collect non-personal data, such as
                      your browser type, device details, and website usage
                      patterns, when you visit our website.
                    </li>
                  </ul>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6"
                      />
                    </svg>
                    Use of Your Information
                  </span>
                  <ul className="list-disc ml-7">
                    <li>
                      To deliver news updates, newsletters, and other relevant
                      communications.
                    </li>
                    <li>
                      To enhance our website, content, and reader services.
                    </li>
                    <li>
                      To respond to your inquiries, feedback, or complaints.
                    </li>
                    <li>
                      For advertising and promotional activities, conduct only
                      with your explicit consent.
                    </li>
                  </ul>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 9V7a5 5 0 00-10 0v2M5 9h14v10a2 2 0 01-2 2H7a2 2 0 01-2-2V9z"
                      />
                    </svg>
                    Data Sharing and Disclosure
                  </span>
                  <p>
                    We do not sell or rent your personal information to third
                    parties. We may, however, share your data with trusted
                    third-party service providers (e.g., email or web hosting
                    services) strictly for operational purposes required to
                    deliver our services.
                  </p>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="3" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19.4 15a1.65 1.65 0 01.33 1.82A8 8 0 013.27 16.82a1.65 1.65 0 01.33-1.82"
                      />
                    </svg>
                    Cookies and Tracking Technologies
                  </span>
                  <p>
                    Our website, www.amdavadpost.in, may use cookies and similar
                    technologies to improve your browsing experience. You have
                    the option to manage or disable cookies through your browser
                    settings.
                  </p>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <rect width="20" height="14" x="2" y="5" rx="2" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 11h8M8 15h8"
                      />
                    </svg>
                    Data Security
                  </span>
                  <p>
                    We implement reasonable security measures to protect your
                    personal information from unauthorized access, disclosure,
                    alteration, and misuse.
                  </p>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-blue-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2a2 2 0 00-2-2H5a2 2 0 00-2 2v2zm0-14v2a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2z"
                      />
                    </svg>
                    Data Retention
                  </span>
                  <p>
                    We will retain your personal information only for as long as
                    is necessary for the purposes set out in this Privacy Policy
                    and to the extent necessary to comply with our legal
                    obligations, resolve disputes, and enforce our policies.
                  </p>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 20h9"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4h9"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v16"
                      />
                    </svg>
                    Third-Party Websites
                  </span>
                  <p>
                    Our website may contain links to other websites that are not
                    operated by us. We have no control over and assume no
                    responsibility for the content, privacy policies, or
                    practices of any third-party sites or services. We strongly
                    advise you to review the Privacy Policy of every site you
                    visit.
                  </p>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-pink-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 14s1.5 2 4 2 4-2 4-2"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 9h.01M15 9h.01"
                      />
                    </svg>
                    Children's Privacy
                  </span>
                  <p>
                    Our services are not intended for individuals under the age
                    of 13. We do not knowingly collect personally identifiable
                    information from children. If you are a parent or guardian
                    and believe your child has provided us with personal
                    information, please contact us immediately.
                  </p>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-green-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12h.01M12 12h.01M9 12h.01M12 17h.01M12 7h.01"
                      />
                    </svg>
                    Your Data Rights and Choices
                  </span>
                  <ul className="list-disc ml-7">
                    <li>
                      You have the right to request access to, correction of, or
                      deletion of your personal data at any time.
                    </li>
                    <li>
                      You have the right to unsubscribe from our communications
                      at any time.
                    </li>
                  </ul>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3"
                      />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    Updates to This Privacy Policy
                  </span>
                  <p>
                    We reserve the right to update this Privacy Policy
                    periodically. Any modifications will be posted on this page,
                    and the "Last Updated" date will be revised accordingly.
                  </p>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-indigo-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <rect width="20" height="14" x="2" y="5" rx="2" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 11h8M8 15h8"
                      />
                    </svg>
                    Governing Law
                  </span>
                  <p>
                    This Privacy Policy is governed by and construed in
                    accordance with the laws of India.
                  </p>
                </li>
                <li>
                  <span className="font-bold flex items-center mb-1">
                    <svg
                      className="w-5 h-5 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 12v4m0 0v4m0-4h4m-4 0h-4"
                      />
                    </svg>
                    Contact Us
                  </span>
                  <ul className="list-none ml-7 space-y-1">
                    <li className="flex items-center">
                      <MailIcon />
                      <span>
                        Email:{" "}
                        <a
                          href="mailto:amdavadpost.in@gmail.com"
                          className="text-blue-700 underline"
                        >
                          amdavadpost.in@gmail.com
                        </a>
                      </span>
                    </li>
                    <li className="flex items-center">
                      <PhoneIcon />
                      <span>
                        Phone/WhatsApp:{" "}
                        <a
                          href="tel:+919737299098"
                          className="text-blue-700 underline"
                        >
                          +91 97372 99098
                        </a>
                      </span>
                    </li>
                    <li className="flex items-center">
                      <LocationIcon />
                      <span>
                        Office Address: Safal Sumel-8, D-313, Ajit Mill Cross
                        Road, Rakhiyal Road, Ahmedabad – 380023, Gujarat
                      </span>
                    </li>
                  </ul>
                </li>
              </ol>
              <div className="mt-4 text-center text-base font-bold text-red-700">
                Amdavad Post – Truth in Your Hand.
              </div>
            </>
          )}
          {heading === "Terms of Use" && (
            <div className="text-gray-700 text-sm">
              <p className="mb-4">
                These are the Terms of Use for Amdavad Post. Please read them
                carefully before using our website or services.
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>
                  By accessing or using our website, you agree to be bound by
                  these terms.
                </li>
                <li>
                  Content is for informational purposes only and may change
                  without notice.
                </li>
                <li>
                  Unauthorized use of the site may give rise to a claim for
                  damages and/or be a criminal offense.
                </li>
                <li>
                  All trademarks reproduced in this website are acknowledged.
                </li>
                <li>We reserve the right to modify these terms at any time.</li>
              </ul>
              <div className="mt-4 text-center text-base font-bold text-red-700">
                Amdavad Post – Truth in Your Hand.
              </div>
            </div>
          )}
          {heading === "Cookie Policy" && (
            <div className="text-gray-700 text-sm">
              <p className="mb-4">
                This Cookie Policy explains how Amdavad Post uses cookies and
                similar technologies to recognize you when you visit our
                website.
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Cookies are small data files placed on your device.</li>
                <li>
                  We use cookies to improve your browsing experience and analyze
                  site traffic.
                </li>
                <li>You can control cookies through your browser settings.</li>
                <li>By using our site, you consent to our use of cookies.</li>
              </ul>
              <div className="mt-4 text-center text-base font-bold text-red-700">
                Amdavad Post – Truth in Your Hand.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Footer = ({ textData, pdfArticles }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalHeading, setModalHeading] = useState("Privacy Policy");
  const [isEpaperModalOpen, setIsEpaperModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenModal = (heading) => {
    setModalHeading(heading);
    setShowModal(true);
  };

  return (
    <footer className="bg-gray-900 text-gray-100 pt-10 pb-4 mt-10">
      <PrivacyPolicyModal
        open={showModal}
        onClose={() => setShowModal(false)}
        heading={modalHeading}
      />
      <EpaperModal
        pdfArticles={pdfArticles}
        open={isEpaperModalOpen}
        onClose={() => setIsEpaperModalOpen(false)}
      />
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 border-b border-gray-700 pb-8">
          <div>
            <h3 className="text-lg font-bold mb-4 text-red-400">
              {textData.footer.services}
            </h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => setIsEpaperModalOpen(true)}
                  className="hover:text-red-300 transition"
                >
                  {textData.footer.links.epaper}
                </button>
              </li>
              <li>
                <a href="#" className="hover:text-red-300 transition">
                  {textData.footer.links.archives}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4 text-red-400">
              {textData.footer.legal}
            </h3>
            <ul className="space-y-2">
              <li>
                <button
                  className="hover:text-red-300 transition text-left"
                  style={{ textDecoration: "none" }}
                  onClick={() => handleOpenModal("Privacy Policy")}
                  type="button"
                >
                  {textData.footer.links.privacy}
                </button>
              </li>
              <li>
                <button
                  className="hover:text-red-300 transition text-left"
                  style={{ textDecoration: "none" }}
                  onClick={() => handleOpenModal("Terms of Use")}
                  type="button"
                >
                  {textData.footer.links.terms}
                </button>
              </li>
              <li>
                <button
                  className="hover:text-red-300 transition text-left"
                  style={{ textDecoration: "none" }}
                  onClick={() => handleOpenModal("Cookie Policy")}
                  type="button"
                >
                  {textData.footer.links.cookie}
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4 text-red-400">Contact Us</h3>
            <ul className="space-y-2">
              <li className="flex items-center">
                <LocationIcon />
                Office Address: Amdavad Post Safal Sumel-8, D-313 Ajit Mill
                Cross Road, Rakhiyal Road Ahmedabad – 380023, Gujarat
              </li>
              <li className="flex items-center">
                <PhoneIcon />
                <a
                  href="tel:+919737299098"
                  className="hover:text-red-300 transition"
                >
                  +91 97372 99098
                </a>
              </li>
              <li className="flex items-center">
                <MailIcon />
                <a href="mailto:amdavadpost.in@gmail.com" className="hover:text-red-300 transition">
                  amdavadpost.in@gmail.com
                </a>
              </li>
            </ul>
          </div>
          <div className="flex flex-col h-full justify-between">
            <div>
              <h3 className="text-lg font-bold text-red-400 mb-4">
                Social Media
              </h3>
              <div className="flex space-x-4 items-center">
                <a
                  href="https://www.instagram.com/amdavadpost.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="hover:text-red-500 transition"
                >
                  <InstagramIcon />
                </a>
                <a
                  href="https://www.threads.com/@amdavadpost.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Threads"
                  className="hover:text-red-500 transition"
                >
                  <ThreadsIcon />
                </a>
                <a
                  href="https://www.facebook.com/people/Amdavad-Post/61553416502739/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="hover:text-red-500 transition"
                >
                  <FacebookIcon />
                </a>
                <a
                  href="https://x.com/Amdavadpost/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                  className="hover:text-red-500 transition"
                >
                  <TwitterIcon />
                </a>
                <a
                  href="https://www.youtube.com/@amdavadpostnews"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  className="hover:text-red-500 transition"
                >
                  <YoutubeIcon />
                </a>
                <a
                  href="https://share.google/bgTrveX4outq8LfHT"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Google"
                  className="hover:text-red-500 transition"
                >
                  <GoogleIcon />
                </a>
              </div>
            </div>

            <div className="mt-8 md:mt-0 flex items-center justify-end gap-2">
              {/* THIS IS THE FIRST CHANGED LINE:
                Changed from w-1/3 to w-2/5
              */}
              <Link
                to="/"
                onClick={(e) => {
                  // If already on home, prevent navigation and scroll to top
                  if (location.pathname === "/") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    window.dispatchEvent(new Event("closeNav"));
                    return;
                  }
                  // Otherwise dispatch event to close nav and allow Link to navigate
                  window.dispatchEvent(new Event("closeNav"));
                  // After navigation, scroll to top quickly
                  setTimeout(
                    () => window.scrollTo({ top: 0, behavior: "smooth" }),
                    60
                  );
                }}
                className="w-4/5"
              >
              <div className="w-4/5">
                <img
                  src="/guj_logo1.png"
                  alt="Gujarat Logo"
                  className="max-w-full max-h-full object-contain bg-transparent cursor-pointer"
                />
              </div>
              </Link>

              {/* THIS IS THE SECOND CHANGED LINE:
                Changed from w-2/3 to w-3/5
              */}
              <Link
                to="/"
                onClick={(e) => {
                  // If already on home, prevent navigation and scroll to top
                  if (location.pathname === "/") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    window.dispatchEvent(new Event("closeNav"));
                    return;
                  }
                  // Otherwise dispatch event to close nav and allow Link to navigate
                  window.dispatchEvent(new Event("closeNav"));
                  // After navigation, scroll to top quickly
                  setTimeout(
                    () => window.scrollTo({ top: 0, behavior: "smooth" }),
                    60
                  );
                }}
                className="w-1/5"
              >
                <div>
                  <img
                    src="/logo.jpg"
                    alt="Amdavad Post Logo"
                    className="max-w-full max-h-full object-contain bg-transparent cursor-pointer"
                  />
                </div>
              </Link>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center pt-6 text-xs text-gray-400">
          <span>{textData.footer.copyright}</span>
          <span>© 2023 amdavadpost.in</span>
        </div>
      </div>
    </footer>
  );
};
// --- Main Page Component ---
function MainSite({ lang, articles, textData, imageVersion }) {
  if (!articles || articles.length === 0) return null;

  const nonEpaperArticles = useMemo(
    () => articles.filter((article) => !article.pdf),
    [articles]
  );

  const { heroArticle, topNews, trendingNews } = useMemo(() => {
    const validArticles = nonEpaperArticles.filter(
      (article) =>
        article.title &&
        article.title.trim() !== "" &&
        article.content &&
        article.content.trim() !== "" &&
        ((article.image && article.image.length > 0) ||
          article.youtubeUrl ||
          article.shortVideo)
    );

    const hero = validArticles[0] || null;
    const top = validArticles.slice(1, 5);

    const remainingForTrending = nonEpaperArticles.filter(
      (article) =>
        article.id !== hero?.id && !top.some((tn) => tn.id === article.id)
    );

    return {
      heroArticle: hero,
      topNews: top,
  trendingNews: remainingForTrending.slice(0, 6),
    };
  }, [nonEpaperArticles]);

  const uniqueVideos = useMemo(() => {
    const videoMap = new Map();
    nonEpaperArticles.forEach((article) => {
      if (article.youtubeUrl) {
        const videoId = getYouTubeVideoId(article.youtubeUrl);
        if (!videoMap.has(videoId)) {
          videoMap.set(videoId, article);
        }
      }
    });
    return Array.from(videoMap.values());
  }, [nonEpaperArticles]);

  const uniqueShorts = useMemo(() => {
    const shortMap = new Map();
    nonEpaperArticles.forEach((article) => {
      if (article.shortVideo) {
        const videoId = getYouTubeVideoId(article.shortVideo);
        if (!shortMap.has(videoId)) {
          shortMap.set(videoId, article);
        }
      }
    });
    return Array.from(shortMap.values());
  }, [nonEpaperArticles]);

  return (
    <main className="flex-grow">
      <MetaTags
        title="Home"
        description="Get the latest news from Ahmedabad, Gujarat, India and around the world. Live updates on business, sports, entertainment, and more."
      />
      <HeroSection
        article={heroArticle}
        textData={textData.heroArticle}
        lang={lang}
        imageVersion={imageVersion}
      />
      <NewsSection
        topNews={topNews}
        trendingNews={trendingNews}
        headings={textData.headings}
        lang={lang}
        imageVersion={imageVersion}
      />
      <VideosSection
        videos={uniqueVideos}
        headings={textData.headings}
        lang={lang}
      />
      <ReelsSection
        reels={uniqueShorts}
        headings={textData.headings}
        lang={lang}
      />
    </main>
  );
}

// --- Layout for public pages ---
const MainLayout = ({
  lang,
  setLang,
  textData,
  ticker,
  pdfArticles,
  imageVersion,
}) => (
  <div className="min-h-screen flex flex-col bg-gray-50">
    <Header lang={lang} setLang={setLang} textData={textData} ticker={ticker} />
    <Outlet />
    <Footer textData={textData} pdfArticles={pdfArticles} />
  </div>
);

// --- Main App Component (Handles State and Routing) ---
export default function App() {
  const [lang, setLang] = useState("en");
  const [articles, setArticles] = useState([]);
  const [ticker, setTicker] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageVersion, setImageVersion] = useState(Date.now());

  const textData = mockData[lang];

  useEffect(() => {
    const fetchNews = async (isUpdate = false) => {
      try {
        if (!isUpdate) {
          setLoading(true);
        }
        const response = await fetch(getApiUrl("GET_NEWS"), {
          cache: "no-store",
        });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.success && Array.isArray(data.posts)) {
          setArticles(data.posts);
          setTicker(data.posts.slice(0, 5).map((p) => p.title));
          if (isUpdate) {
            setImageVersion(Date.now());
          }
        } else {
          throw new Error("Invalid data structure from API");
        }
      } catch (e) {
        if (!isUpdate) {
          setError(e.message);
        } else {
          // Silently log error on auto-refresh to not disrupt user
          console.error("Failed to auto-refresh articles:", e);
        }
      } finally {
        if (!isUpdate) {
          setLoading(false);
        }
      }
    };

    fetchNews();

    // Use BroadcastChannel for cross-tab communication
    const channel = new BroadcastChannel("articles-update");
    channel.onmessage = () => {
      fetchNews(true /* isUpdate */);
    };

    return () => {
      channel.close();
    };
  }, []);

  const pdfArticles = useMemo(() => {
    return articles
      .filter((a) => a.pdf)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [articles]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen text-xl">
        Loading News...
      </div>
    );
  if (error)
    return (
      <div className="flex justify-center items-center h-screen text-red-500 text-xl">
        Error: {error}
      </div>
    );

  return (
    <>
      <style>{customStyles}</style>
      <Router>
        <Routes>
          <Route
            element={
              <MainLayout
                lang={lang}
                setLang={setLang}
                textData={textData}
                ticker={ticker}
                pdfArticles={pdfArticles}
                imageVersion={imageVersion}
              />
            }
          >
            <Route
              path="/"
              element={
                <MainSite
                  lang={lang}
                  articles={articles}
                  textData={textData}
                  imageVersion={imageVersion}
                />
              }
            />
            <Route
              path="/videos"
              element={<VideosPage allArticles={articles} lang={lang} />}
            />
            <Route
              path="/shorts"
              element={<ShortsPage allArticles={articles} lang={lang} />}
            />
            <Route
              path="/category/:categoryName"
              element={
                <CategoryPage
                  allArticles={articles}
                  lang={lang}
                  textData={textData}
                  imageVersion={imageVersion}
                />
              }
            />
            <Route
              path="/news/:slug"
              element={
                <SingleNewsPage
                  allArticles={articles}
                  lang={lang}
                  textData={textData}
                  imageVersion={imageVersion}
                />
              }
            />
            <Route
              path="/epaper"
              element={<EpaperPage allArticles={articles} />}
            />
            <Route
              path="/epaper/:articleId"
              element={<EpaperPage allArticles={articles} />}
            />
          </Route>

          {/* Routes without Header/Footer */}
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="/login" element={<UserLogin />} />
          <Route path="/signup" element={<UserSignup />} />
        </Routes>
      </Router>
    </>
  );
}
